import React, { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material'
import { useGetCommoditiesQuery, useGetCsvLazyQuery, usePostCsvMutation } from 'services/commodities'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import Loading from 'components/generic/Loading'
import { setSnackBar } from 'state/ui'
import { COMMODITIES_ACTIONS } from 'utils/ui/actions'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import { downloader } from 'utils/helpers'
import CommoditiesListDetail from './CommoditiesListDetail'

export default function CommoditiesContainer() {
  const context = useContext(ContentLayoutContext)
  const today = new Date()
  const { organization, selectedYard } = useSelector(state => state.auth)
  const { data: commodities, isLoading: isLoadingCommodities } = useGetCommoditiesQuery({
    pageSize: 1000,
  })
  const [selectedCommodityId, setSelectedCommodityId] = useState(null)

  const [getCsv, { data: csvData, isLoading: isLoadingGetCsv, requestId, isError, error: getCsvError, isSuccess }] =
    useGetCsvLazyQuery()
  const [postCsv, { isLoading: isLoadingPostCsv }] = usePostCsvMutation()

  const dispatch = useDispatch()

  const { canDownloadAndUploadPrices, enableCommoditiesListDetailView } = useSelector(
    state => state.featureFlags?.variables || {},
  )

  useEffect(() => {
    if (!csvData) return
    downloader(
      csvData,
      'text/csv',
      `${organization}-${selectedYard}-${today.toISOString().split('T')[0]}-material-prices.csv`,
    )
  }, [csvData, requestId])

  const handleSuccess = isUpload => {
    trackEvent(isUpload ? cz.UPLOAD_CSV : cz.DOWNLOAD_CSV)
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: `CSV file ${isUpload ? 'uploaded' : 'downloaded'} successfully`,
      }),
    )
  }

  const handleError = (error, isUpload) => {
    const errorList = error?.data?.errorsMessage || []
    const errorListString = errorList.join(', ')
    const errorMsg = errorList?.length
      ? `CSV ${isUpload ? 'upload' : 'download'} failed.${isUpload ? ` Please update the following errors and try again: ${errorListString}` : ''}`
      : error?.data?.error || `There was an error ${isUpload ? 'uploading' : 'downloading'} the CSV file`
    trackEvent({
      eventName: isUpload ? cz.UPLOAD_CSV : cz.DOWNLOAD_CSV,
      errorMsg,
    })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: errorMsg,
      }),
    )
  }

  useEffect(() => {
    if (isSuccess) {
      handleSuccess(false)
    }
    if (isError) {
      handleError(getCsvError, false)
    }
  }, [isError, isSuccess, requestId])

  const handleGetCsv = () => getCsv()

  const uploaderBtnRef = useRef()
  const handleButtonClick = () => {
    uploaderBtnRef.current.click()
  }
  const submitCsvFile = async event => {
    const selectedCsv = event.target.files[0]
    if (!selectedCsv) return

    const file = new FormData()
    file.append('data', selectedCsv, 'priceSheetFile.csv')
    await postCsv({ body: file })
      .unwrap()
      .then(() => {
        handleSuccess(true)
      })
      .catch(err => {
        handleError(err, true)
      })
  }

  const csvActions = [
    {
      icon: <CloudUploadIcon />,
      text: 'Upload Prices',
      id: 'upload-prices',
      variant: 'text',
      onClick: handleButtonClick,
    },
    {
      icon: <CloudDownloadIcon />,
      text: 'Download Prices',
      id: 'download-prices',
      variant: 'text',
      onClick: handleGetCsv,
    },
  ]

  useEffect(() => {
    context.setContainerTitle('Commodities')
    const pageActions = [...COMMODITIES_ACTIONS]
    if (canDownloadAndUploadPrices) {
      pageActions.unshift(...csvActions)
    }
    context.setContainerActions(pageActions)
  }, [])

  if (isLoadingGetCsv || isLoadingPostCsv) return <Loading />
  if (isLoadingCommodities) return <Loading localized />

  return (
    <>
      <input
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        ref={uploaderBtnRef}
        onChange={e => {
          submitCsvFile(e)
        }}
      />
      <Box py={1} backgroundColor="background.section">
        <CommoditiesListDetail
          selectedCommodityId={selectedCommodityId}
          setSelectedCommodityId={setSelectedCommodityId}
          commodities={commodities?.Items || []}
        />
      </Box>
    </>
  )
}
